"use client"

import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react"
import { createPortal } from "react-dom"

import { classNames } from "../../../../lib/classNames"

import type { PropsWithChildren, RefObject } from "react"

export interface ModalShellProps {
  open: boolean
  onClose(): void
  // disableDismiss keeps the dialog open on backdrop clicks and Escape, so it
  // can only be closed through its own controls. For flows that must not be
  // dismissed accidentally (e.g. recovering a captured payment).
  disableDismiss?: boolean
  size?: "xs" | "sm" | "md" | "lg" | "xl"
  initialFocus?: RefObject<HTMLElement | null>
  scrollable?: boolean
  blurBg?: boolean
  className?: string
  panelClassName?: string
  topPaddingOnly?: boolean
  portalRoot?: HTMLElement
}

export function ModalShell({
  open,
  onClose,
  disableDismiss,
  size = "sm",
  initialFocus,
  scrollable = true,
  blurBg,
  className,
  panelClassName,
  topPaddingOnly,
  portalRoot,
  children,
}: PropsWithChildren<ModalShellProps>) {
  const panel = (
    <Dialog
      open={open}
      onClose={disableDismiss ? () => {} : onClose}
      initialFocus={initialFocus}
      className={classNames("z-50", className)}
    >
      <DialogBackdrop
        aria-hidden
        className={classNames(
          "bg-v2-background-scrim fixed inset-0 z-50",
          blurBg && "backdrop-blur-xs"
        )}
      />
      <div className="fixed inset-0 z-50 flex w-full items-center justify-center p-4">
        <DialogPanel
          className={classNames(
            "bg-v2-background-elevation-modal shadow-v2-modal rounded-overlay max-h-full w-full p-6",
            scrollable && "overflow-y-auto",
            size === "xs" && "max-w-135",
            size === "sm" && "max-w-160",
            size === "md" && "max-w-200",
            size === "lg" && "max-w-250",
            size === "xl" && "max-w-320",
            topPaddingOnly && "p-0 pt-6",
            panelClassName
          )}
        >
          {children}
        </DialogPanel>
      </div>
    </Dialog>
  )

  return portalRoot ? createPortal(panel, portalRoot) : panel
}
